/**
 * CopilotProvider — mounts the floating CopilotWidget and injects the
 * react-router `navigate` function into the action executor.
 *
 * Must be rendered INSIDE the router tree (any layout/route component).
 * It does NOT need to wrap the <Outlet/>: the widget renders through a
 * portal on document.body, and pages talk to the copilot via the
 * out-of-React context registry (useCopilotPage) and window CustomEvents
 * ('copilot:fill', 'copilot:submit', 'copilot:highlight', …) — no React
 * context is involved. `children` is supported but optional.
 */

import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { setExecutorHelpers } from './action-executor';
import { CopilotWidget } from './CopilotWidget';

export function CopilotProvider({ children }: { children?: ReactNode }) {
  const navigate = useNavigate();

  useEffect(() => {
    setExecutorHelpers({
      navigate: (to: string) => {
        void navigate(to);
      },
    });
  }, [navigate]);

  return (
    <>
      {children}
      <CopilotWidget />
    </>
  );
}

// Convenience re-export so pages can `import { useCopilotPage } from
// '@/features/copilot/CopilotProvider'` (also exported from the barrel).
export { useCopilotPage } from './context-registry';
